import { useEffect, useRef, useState } from 'react';
import { useGetUsersNeedHelpMutation } from '../../../store/chatbox/chatboxApi';
import { IPaginationPayload } from '../../../interfaces/shared/IPaginationPayload';
import { resolveBillyHasMore, resolveBillyPaginatedData } from '../../../utils/pagination/billyPagination';
import UsersNeedHelpFilters from './components/UsersNeedHelpFilters';
import UsersNeedHelpTable from './components/UsersNeedHelpTable';

const UsersNeedHelp = () => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const [paginationState, setPaginationState] = useState<IPaginationPayload>({
        pageNumber: 1,
        pageSize: 10,
    });

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [filters, setFilters] = useState<{
        dateFrom?: string;
        dateTo?: string;
    }>({
        dateFrom: undefined,
        dateTo: undefined,
    });

    const [rows, setRows] = useState<any[]>([]);
    const [pageData, setPageData] = useState<any>(undefined);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const [getUsersNeedHelp] = useGetUsersNeedHelpMutation();

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
        }, 400);

        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        setRows([]);
        setHasMore(true);
        setPaginationState((prev) => ({
            ...prev,
            pageNumber: 1,
        }));
    }, [debouncedSearch, filters.dateFrom, filters.dateTo]);

    useEffect(() => {
        const fetchData = async () => {
            const isFirstPage = paginationState.pageNumber === 1;

            try {
                if (isFirstPage) {
                    setLoading(true);
                } else {
                    setLoadingMore(true);
                }

                const result = await getUsersNeedHelp({
                    ...paginationState,
                    data: {
                        search: debouncedSearch || undefined,
                        dateFrom: filters.dateFrom,
                        dateTo: filters.dateTo,
                    }
                }).unwrap();

                const { elements, page } = resolveBillyPaginatedData<any>(result);

                setPageData(page);

                setRows((prev) => (
                    isFirstPage ? elements : [...prev, ...elements]
                ));

                setHasMore(resolveBillyHasMore(page, elements.length, paginationState.pageSize || 10));
            } catch (e) {
                console.error({ e });
                if (isFirstPage) {
                    setRows([]);
                    setPageData(undefined);
                }
                setHasMore(false);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        };

        fetchData();
    }, [
        paginationState.pageNumber,
        paginationState.pageSize,
        debouncedSearch,
        filters.dateFrom,
        filters.dateTo,
        getUsersNeedHelp,
    ]);

    const handleScroll = () => {
        if (!scrollContainerRef.current || !hasMore || loadingMore || loading) return;
        const el = scrollContainerRef.current;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
            setPaginationState((prev) => ({
                ...prev,
                pageNumber: prev.pageNumber + 1,
            }));
        }
    };

    return (
        <div>
            <h1 className='text-xl md:text-2xl font-bold text-[#101010] dark:text-primary-darkMode'>
                Requests for Help
            </h1>

            <p className='mt-2 text-sm text-gray-600 dark:text-gray-400'>
                Review user help requests.
            </p>

            <UsersNeedHelpFilters
                totalElements={pageData?.totalElements ?? 0}
                search={search}
                onSearchChange={setSearch}
                dateFrom={filters.dateFrom}
                dateTo={filters.dateTo}
                onDateChange={setFilters}
            />

            <UsersNeedHelpTable
                rows={rows}
                loading={loading}
                loadingMore={loadingMore}
                hasMore={hasMore}
                scrollContainerRef={scrollContainerRef}
                onScrollEnd={handleScroll}
            />
        </div>
    );
};

export default UsersNeedHelp;
